import { useEffect, useState } from "react";
import { AdvanceSearch, ICConfirmationModal } from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  activeInactiveClaimGroup,
  deleteClaimGroup,
  getAllClaimGroup,
  IClaimGroup,
  IClaimOtherGroup,
} from "./ClaimGroupController";
import {
  deleteIcon,
  editIcon,
  timeLineIcon,
} from "../../assets/icons/SvgIconList";
import { ICSwitch } from "../../core-component/form";
import Loader from "../../core-component/ui/Loader";
import { AUDIT_LOG_TYPE, BIT_VALUE } from "../../utils/AppEnumerations";
import {
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import { routeAdmin, routeMaster } from "../../routes/AuthRoutePath";
import { useNavigate } from "react-router-dom";
import AuditLog from "../audit-log/AuditLogView";
import { cn } from "../../lib";

let timer: ReturnType<typeof setTimeout>;

const ClaimGroupView = () => {
  const { t } = UseTranslationHook();
  const navigate = useNavigate();
  const [isLoading, setISLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [claimGroupList, setClaimGroupList] = useState<IClaimGroup[]>([]);
  const [claimOtherGroupList, setClaimOtherGroupList] = useState<
    IClaimOtherGroup[]
  >([]);
  const [claimGroupToView, setClaimGroupToView] = useState<IClaimGroup>();
  const [showAuditLog, setShowAuditLog] = useState(false);

  useEffect(() => {
    fetchClaimGroup();
  }, []);

  const fetchClaimGroup = () => {
    setISLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await getAllClaimGroup(setClaimGroupList, setClaimOtherGroupList);
      setISLoading(false);
    }, 500);
  };

  const handleClaimGroupStatus = async (values: IClaimGroup) => {
    const isStatusChange =
      values.is_deleted === BIT_VALUE.Zero &&
      (await activeInactiveClaimGroup(
        values.id,
        values.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One,
      ));
    if (isStatusChange) {
      setClaimGroupList(toggleStatus(claimGroupList, values.id));
    }
  };

  const handleDelete = async (value: boolean) => {
    if (value && claimGroupToView) {
      const data = await deleteClaimGroup(claimGroupToView.id);
      if (data) {
        fetchClaimGroup();
      }
    }
    setShowDeleteConfirmation(false);
  };

  const handleCloseForm = (status: boolean) => {
    setShowAuditLog(false);
    if (claimGroupToView) {
      setClaimGroupToView(undefined);
    }
  };
  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="w-full overflow-auto scrollbar-remove p-4">
          <AdvanceSearch
            handleOnClickAdd={() =>
              navigate(
                "/" + routeMaster.admin + "/" + routeAdmin.claimGroupForm,
              )
            }
            refreshTable={() => {
              fetchClaimGroup();
            }}
            title={t("claim_group")}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {claimGroupList &&
              claimGroupList.length > 0 &&
              claimGroupList.map((item, index) => (
                <div key={item.id} className="p-4 bg-subtle rounded-md ">
                  <div className="bg-accent-muted rounded-t-md">
                    <div className="flex items-center justify-between px-2 py-1 border-b-2 border-muted">
                      <h2 className="text-lg font-medium ">
                        {item.claim_group_name}
                      </h2>
                      <div className="flex">
                        <div className="pr-2">
                          <ICSwitch
                            checked={item.is_active === BIT_VALUE.One}
                            onChange={() => handleClaimGroupStatus(item)}
                          />
                        </div>
                        <div
                          className="ml-2 size-5 cursor-pointer"
                          title={t("edit_tooltip_icon")}
                          onClick={() => {
                            navigate(
                              "/" +
                                routeMaster.admin +
                                "/" +
                                routeAdmin.claimGroupForm +
                                "/" +
                                item.id,
                            );
                          }}
                        >
                          {editIcon}
                        </div>
                        <div
                          className="ml-2 size-5 cursor-pointer"
                          title={t("delete_tooltip_icon")}
                          onClick={() => {
                            setShowDeleteConfirmation(true);
                            setClaimGroupToView(item);
                          }}
                        >
                          {deleteIcon}
                        </div>
                        <div
                          className="ml-2 size-5 cursor-pointer"
                          title={t("audit_log_tooltip_icon")}
                          onClick={() => {
                            setClaimGroupToView(item);
                            setShowAuditLog(true);
                          }}
                        >
                          {timeLineIcon}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    {item.claims.length > 0 &&
                      item.claims.map((claim, claimIndex) => (
                        <div key={claim.id} className="flex items-start">
                          <span className="text-invert flex-shrink-0 mr-3">
                            ❈
                          </span>
                          <div className="leading-tight mt-0.5">
                            {claim.claim_name}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            <div
              className={cn({
                "lg:col-span-2": claimGroupList.length % 2 === 0,
              })}
            >
              {claimOtherGroupList && claimOtherGroupList.length > 0 && (
                <div className="p-4 bg-subtle rounded-md border-b-2 border-muted">
                  <div className="bg-accent-muted rounded-t-md">
                    <h2 className="text-lg font-medium px-2 py-1">
                      {t("other_claims")}
                    </h2>
                  </div>

                  <div
                    className={cn(
                      "grid grid-cols-1 sm:grid-cols-2  gap-4 pt-4",
                      {
                        "sm:grid-cols-4": claimGroupList.length % 2 === 0,
                      },
                    )}
                  >
                    {claimOtherGroupList &&
                      claimOtherGroupList.length > 0 &&
                      claimOtherGroupList.map((item, index) => (
                        <div key={item.id} className="flex items-start">
                          <span className="text-invert flex-shrink-0 mr-3">
                            ❈
                          </span>
                          <div className="leading-tight">{item.claim_name}</div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {showAuditLog && claimGroupToView && (
            <AuditLog
              handleClose={handleCloseForm}
              auditLogRecordId={claimGroupToView.id}
              auditLogType={AUDIT_LOG_TYPE.ClaimGroup}
              titleName={t("claim_group")}
            />
          )}
          {showDeleteConfirmation && claimGroupToView && (
            <ICConfirmationModal
              title={t("delete_claim_group")}
              message={prepareMessageFromParams(t("delete_message"), [
                ["fieldName", claimGroupToView.claim_group_name],
              ])}
              onClose={(value) => handleDelete(value)}
            />
          )}
        </div>
      )}
    </>
  );
};
export default ClaimGroupView;
